import React, { useState } from 'react';
import {
  Share2,
  Copy,
  Check,
  MessageCircle,
  Facebook,
  Twitter,
  Send,
  Linkedin,
} from 'lucide-react';
import { useApp } from '../../context/AppContext.tsx';

interface SocialShareButtonsProps {
  title: string;
  summary?: string;
  url?: string;
  imageUrl?: string;
  variant?: 'compact' | 'full' | 'banner';
  className?: string;
}

export default function SocialShareButtons({
  title,
  summary = '',
  url,
  imageUrl,
  variant = 'full',
  className = '',
}: SocialShareButtonsProps) {
  const { showToast } = useApp();
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const cleanSummary = summary ? summary.replace(/(<([^>]+)>)/gi, '').slice(0, 160) : '';

  // Formatted message text for WA & Telegram with thumbnail notice or title
  const whatsappMessage = `${title}\n\n${cleanSummary ? `"${cleanSummary}"\n\n` : ''}Read more at:\n${shareUrl}`;
  const telegramText = `${title}\n${cleanSummary}`;

  const handleShare = (platform: 'whatsapp' | 'facebook' | 'twitter' | 'telegram' | 'linkedin' | 'copy') => {
    switch (platform) {
      case 'whatsapp':
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappMessage)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;

      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;

      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;

      case 'telegram':
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(telegramText)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;

      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;

      case 'copy':
        if (navigator.clipboard) {
          navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          showToast('Link copied to clipboard!', 'success', 'Link Copied');
          setTimeout(() => setCopied(false), 2500);
        }
        break;
    }
  };

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-1.5 flex-wrap ${className}`}>
        {/* WhatsApp */}
        <button
          type="button"
          onClick={() => handleShare('whatsapp')}
          className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
          title="Share on WhatsApp"
        >
          <MessageCircle className="w-4 h-4" />
        </button>

        {/* Facebook */}
        <button
          type="button"
          onClick={() => handleShare('facebook')}
          className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          title="Share on Facebook"
        >
          <Facebook className="w-4 h-4" />
        </button>

        {/* Twitter/X */}
        <button
          type="button"
          onClick={() => handleShare('twitter')}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200 transition-colors"
          title="Share on X / Twitter"
        >
          <Twitter className="w-4 h-4" />
        </button>

        {/* Telegram */}
        <button
          type="button"
          onClick={() => handleShare('telegram')}
          className="p-1.5 rounded-lg bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors"
          title="Share on Telegram"
        >
          <Send className="w-4 h-4" />
        </button>

        {/* LinkedIn */}
        <button
          type="button"
          onClick={() => handleShare('linkedin')}
          className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
          title="Share on LinkedIn"
        >
          <Linkedin className="w-4 h-4" />
        </button>

        {/* Copy Link */}
        <button
          type="button"
          onClick={() => handleShare('copy')}
          className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-bold flex items-center gap-1 transition-colors"
          title="Copy Link"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-emerald-700">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 text-slate-500" />
              <span>Copy Link</span>
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={`p-4 sm:p-5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-extrabold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
          <Share2 className="w-4 h-4 text-emerald-600" />
          <span>Share This Article (Social & OpenGraph)</span>
        </span>
        <span className="text-[10px] text-slate-400 font-medium">OpenGraph Preview Enabled</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* WhatsApp */}
        <button
          type="button"
          onClick={() => handleShare('whatsapp')}
          className="px-3.5 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 font-bold text-xs flex items-center gap-2 shadow-xs transition-colors shrink-0"
        >
          <MessageCircle className="w-4 h-4" />
          <span>WhatsApp</span>
        </button>

        {/* Facebook */}
        <button
          type="button"
          onClick={() => handleShare('facebook')}
          className="px-3.5 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 font-bold text-xs flex items-center gap-2 shadow-xs transition-colors shrink-0"
        >
          <Facebook className="w-4 h-4" />
          <span>Facebook</span>
        </button>

        {/* Twitter / X */}
        <button
          type="button"
          onClick={() => handleShare('twitter')}
          className="px-3.5 py-2 rounded-xl bg-slate-900 text-white hover:bg-black font-bold text-xs flex items-center gap-2 shadow-xs transition-colors shrink-0"
        >
          <Twitter className="w-4 h-4" />
          <span>X / Twitter</span>
        </button>

        {/* Telegram */}
        <button
          type="button"
          onClick={() => handleShare('telegram')}
          className="px-3.5 py-2 rounded-xl bg-sky-500 text-white hover:bg-sky-600 font-bold text-xs flex items-center gap-2 shadow-xs transition-colors shrink-0"
        >
          <Send className="w-4 h-4" />
          <span>Telegram</span>
        </button>

        {/* LinkedIn */}
        <button
          type="button"
          onClick={() => handleShare('linkedin')}
          className="px-3.5 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-bold text-xs flex items-center gap-2 shadow-xs transition-colors shrink-0"
        >
          <Linkedin className="w-4 h-4" />
          <span>LinkedIn</span>
        </button>

        {/* Copy Link Button */}
        <button
          type="button"
          onClick={() => handleShare('copy')}
          className="px-3.5 py-2 rounded-xl bg-white text-slate-800 border border-slate-300 hover:bg-slate-100 font-bold text-xs flex items-center gap-2 shadow-2xs transition-colors shrink-0"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-emerald-700">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 text-slate-500" />
              <span>Copy Link</span>
            </>
          )}
        </button>
      </div>

      {imageUrl && (
        <div className="text-[10px] text-slate-400 flex items-center gap-1.5 pt-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span>Automatic photo thumbnail enabled for social sharing</span>
        </div>
      )}
    </div>
  );
}
